/**
 * Utilitaire de gestion du localStorage avec expiration automatique (12 mois).
 * Conforme RGPD/nLPD suisse - Sprint C1-StorageHygiene.
 */

const EXPIRY_MONTHS = 12;

/**
 * Enregistre une valeur avec date d'expiration (12 mois).
 * @param {string} key - Clé de stockage
 * @param {string} value - Valeur à stocker
 */
export function setWithExpiry(key, value) {
  try {
    const now = new Date();
    const expiryDate = new Date(now.setMonth(now.getMonth() + EXPIRY_MONTHS));
    
    const item = {
      value: value,
      expiry: expiryDate.getTime(),
    };
    
    localStorage.setItem(key, JSON.stringify(item));
  } catch (e) {
    // Silently fail si localStorage est désactivé
    console.warn(`[storage] Impossible d'enregistrer ${key}:`, e);
  }
}

/**
 * Récupère une valeur si elle n'est pas expirée.
 * Supprime automatiquement si expirée.
 * @param {string} key - Clé de stockage
 * @returns {string|null} - Valeur ou null si expirée/inexistante
 */
export function getWithExpiry(key) {
  try {
    const itemStr = localStorage.getItem(key);
    if (!itemStr) return null;
    
    // Support ancien format (string simple) pour rétrocompatibilité
    try {
      const item = JSON.parse(itemStr);
      
      // Si pas de champ expiry, c'est l'ancien format
      if (!item.expiry) {
        // Migrer vers nouveau format avec expiration
        setWithExpiry(key, itemStr);
        return itemStr;
      }
      
      // Vérifier expiration
      const now = new Date().getTime();
      if (now > item.expiry) {
        localStorage.removeItem(key);
        return null;
      }
      
      return item.value;
    } catch {
      // Si le JSON parse échoue, c'est une valeur simple (ancien format)
      setWithExpiry(key, itemStr);
      return itemStr;
    }
  } catch (e) {
    console.warn(`[storage] Impossible de lire ${key}:`, e);
    return null;
  }
}

/**
 * Supprime une clé du localStorage.
 * @param {string} key - Clé à supprimer
 */
export function removeItem(key) {
  try {
    localStorage.removeItem(key);
  } catch (e) {
    console.warn(`[storage] Impossible de supprimer ${key}:`, e);
  }
}

/**
 * Nettoie toutes les clés expirées du localStorage.
 * À appeler au démarrage de l'application.
 */
export function cleanupExpiredItems() {
  try {
    const keys = Object.keys(localStorage);
    let cleanedCount = 0;
    
    keys.forEach((key) => {
      // Ignorer les clés non-body-up
      if (!key.startsWith('bodyup_') && !key.startsWith('bodyup-') && !key.startsWith('trainiq-')) {
        return;
      }
      
      const itemStr = localStorage.getItem(key);
      if (!itemStr) return;
      
      try {
        const item = JSON.parse(itemStr);
        if (item.expiry) {
          const now = new Date().getTime();
          if (now > item.expiry) {
            localStorage.removeItem(key);
            cleanedCount++;
          }
        }
      } catch {
        // Pas un objet JSON, ignorer
      }
    });
    
    if (cleanedCount > 0) {
      console.info(`[storage] ${cleanedCount} élément(s) expiré(s) supprimé(s)`);
    }
  } catch (e) {
    console.warn('[storage] Erreur lors du nettoyage:', e);
  }
}

/**
 * Nettoie les anciennes clés dépréciées (C1-StorageHygiene).
 * À appeler une seule fois au démarrage.
 */
export function cleanupDeprecatedKeys() {
  try {
    const deprecatedKeys = [
      'bodyup_lead_email',      // Plus utilisé (pré-remplissage email calculateur)
      'bodyup_avatar_sex',      // Plus utilisé (avatar neutre maintenant)
    ];
    
    deprecatedKeys.forEach((key) => {
      if (localStorage.getItem(key)) {
        localStorage.removeItem(key);
        console.info(`[storage] Clé dépréciée supprimée: ${key}`);
      }
    });
  } catch (e) {
    console.warn('[storage] Erreur lors du nettoyage des clés dépréciées:', e);
  }
}
